import React from "react";
import { ASSETS, BUSINESS } from "../lib/assets";
import SectionHeading from "../components/SectionHeading";
import usePageMeta from "../hooks/usePageMeta";
import { Link } from "react-router-dom";
import { Trophy, Flame, Users, Award } from "lucide-react";

export default function About() {
  usePageMeta({
    title: "About the Family",
    description: "Meet the family behind When Pigs Fly Family BBQ — an award-winning slow-smoked BBQ operation rooted in Spring Hill, FL and serving the Nature Coast.",
    path: "/about",
  });
  return (
    <div>
      {/* HERO */}
      <section className="border-b border-ink py-16 md:py-24" data-testid="about-hero">
        <div className="mx-auto grid max-w-[1400px] grid-cols-1 gap-10 px-4 md:grid-cols-12 md:px-8">
          <div className="md:col-span-7">
            <div className="eyebrow mb-3 text-ink/70">Who we are</div>
            <h1 className="font-rye text-5xl uppercase leading-[0.9] sm:text-6xl lg:text-7xl">
              About When Pigs Fly<br />Family BBQ
            </h1>
            <p className="mt-6 max-w-2xl font-serif text-lg leading-relaxed text-ink/85">
              At When Pigs Fly Family BBQ, barbecue isn't just food — it's our passion, our tradition,
              and our way of bringing people together.
            </p>
          </div>
          <div className="md:col-span-5">
            <div className="grain-block relative aspect-[4/5] overflow-hidden border border-ink shadow-hard">
              <img src={ASSETS.teamKcbs} alt="The family team" className="mono-img h-full w-full object-cover" />
            </div>
          </div>
        </div>
      </section>

      {/* AWARDS STRIP */}
      <section className="border-b border-ink bg-ink py-14 text-bone" data-testid="about-awards">
        <div className="mx-auto grid max-w-[1400px] grid-cols-1 gap-8 px-4 md:grid-cols-3 md:px-8">
          {[
            { icon: Trophy, k: "Multiple", v: "Grand Championships" },
            { icon: Award, k: "Top 20", v: "Best Seasonings on the Planet · American Royal BBQ Contest" },
            { icon: Flame, k: "Championship", v: "Sweet Perfection Rub — Award-winning" },
          ].map((a, i) => {
            const Ico = a.icon;
            return (
              <div key={i} className="flex items-start gap-4 border-l-2 border-bone pl-4">
                <Ico size={24} className="mt-1 shrink-0" />
                <div>
                  <div className="font-alfa text-3xl uppercase leading-none">{a.k}</div>
                  <div className="mt-2 font-mono text-[11px] uppercase tracking-[0.2em] text-bone/80">{a.v}</div>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* THE STORY */}
      <section className="border-b border-ink py-20 md:py-28">
        <div className="mx-auto grid max-w-[1400px] grid-cols-1 gap-10 px-4 md:grid-cols-12 md:px-8">
          <div className="md:col-span-5">
            <SectionHeading eyebrow="Our story" title="From Passion to Pit" />
          </div>
          <div className="md:col-span-7 font-serif text-base leading-relaxed text-ink/85 space-y-6">
            <p>
              What started as a love for competition barbecue has grown into an award-winning family business
              dedicated to serving authentic, slow-smoked BBQ to our community. Our team has earned multiple
              <strong> Grand Championships</strong>, competed on some of the biggest stages in barbecue, and our signature
              <strong> Sweet Perfection BBQ Rub</strong> was recognized among the <strong>Top 20 Best Seasonings on
              the Planet</strong> at the American Royal World Series of Barbecue.
            </p>
            <p>
              Every day, we fire up our smokers using traditional low-and-slow techniques to create tender brisket,
              juicy pulled pork, fall-off-the-bone ribs, smoked chicken, handcrafted sides, and homemade desserts.
              Whether you're stopping in for lunch, feeding your family, or catering your next event, our goal is
              simple: serve unforgettable barbecue made with pride.
            </p>
            <p>
              Our award-winning seasoning line brings that same championship flavor to backyards across the country.
              Each blend has been developed and refined through years of competition cooking, giving home cooks and
              pitmasters the confidence to create incredible barbecue every time they light the smoker.
            </p>
            <p>
              As a family-owned business, we're grateful for every customer who walks through our doors, shares our
              story, and supports our dream. From our family to yours — thank you for letting us be part of your table.
            </p>
            <p className="mt-4 border-l-4 border-ink pl-5 font-rye text-3xl uppercase leading-tight md:text-4xl">
              Real BBQ.<br />Made the Florida Man Way.
            </p>
          </div>
        </div>
      </section>

      {/* THE FAMILY — team photo */}
      <section className="border-b border-ink bg-bone py-16 md:py-24" data-testid="about-family-photo">
        <div className="mx-auto max-w-[1400px] px-4 md:px-8">
          <SectionHeading eyebrow="The whole crew" title="The Family" subtitle="Kids in the mix, grown-ups on the pit — competition weekends are a family affair." />
          <figure className="mt-12 grain-block relative overflow-hidden border-2 border-ink shadow-hard">
            <img
              src={ASSETS.familyTeamKcs}
              alt="The When Pigs Fly family team at a Kansas City BBQ Society competition backdrop"
              className="h-full w-full object-cover"
            />
            <figcaption className="border-t border-ink bg-white p-4 font-mono text-[10px] uppercase tracking-[0.2em] text-ink/70">
              The When Pigs Fly crew · Kansas City BBQ Society competition weekend
            </figcaption>
          </figure>
        </div>
      </section>

      {/* WHAT WE COOK */}
      <section className="border-b border-ink bg-bone py-20 md:py-28" data-testid="what-we-cook">
        <div className="mx-auto max-w-[1400px] px-4 md:px-8">
          <SectionHeading eyebrow="From the pit" title="What We Cook Daily" subtitle="Low and slow. Every single day." />
          <div className="mt-12 grid grid-cols-2 gap-0 border border-ink bg-white md:grid-cols-6">
            {[
              "Tender Brisket",
              "Juicy Pulled Pork",
              "Fall-Off-The-Bone Ribs",
              "Smoked Chicken",
              "Handcrafted Sides",
              "Homemade Desserts",
            ].map((item, i) => (
              <div
                key={item}
                data-testid={`daily-${i}`}
                className={`grain-block p-6 border-ink ${((i + 1) % 2 !== 0) ? "border-r" : ""} ${((i + 1) % 6 !== 0) ? "md:border-r" : ""} ${i < 4 ? "border-b md:border-b-0" : ""}`}
              >
                <div className="eyebrow mb-2 text-ink/50">0{i + 1}</div>
                <h4 className="font-alfa text-lg uppercase leading-tight">{item}</h4>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* VALUES */}
      <section className="border-b border-ink py-20 md:py-28">
        <div className="mx-auto max-w-[1400px] px-4 md:px-8">
          <SectionHeading eyebrow="What we stand for" title="The Values" />
          <div className="mt-12 grid grid-cols-1 gap-0 border border-ink md:grid-cols-4">
            {[
              { n: "01", t: "Family First", d: "Every recipe is one we'd serve to our own kids." },
              { n: "02", t: "Low & Slow", d: "No shortcuts. No liquid smoke. Real fire, real time." },
              { n: "03", t: "Championship Craft", d: "Refined on the biggest stages in barbecue." },
              { n: "04", t: "Community Roots", d: "Grateful for every neighbor who shares our story." },
            ].map((v, i) => (
              <div key={v.n} className={`grain-block bg-white p-8 border-ink ${i < 3 ? "md:border-r border-b md:border-b-0" : ""}`}>
                <div className="font-alfa text-4xl uppercase text-ink/30">{v.n}</div>
                <h3 className="mt-3 font-alfa text-xl uppercase">{v.t}</h3>
                <p className="mt-3 font-serif text-sm text-ink/80">{v.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="border-b border-ink bg-ink py-16 text-bone">
        <div className="mx-auto flex max-w-[1400px] flex-col justify-between gap-6 px-4 md:flex-row md:items-center md:px-8">
          <div>
            <div className="eyebrow mb-2 text-bone/60">Come eat</div>
            <h2 className="font-alfa text-3xl uppercase md:text-4xl">Visit The Flagship</h2>
            <p className="mt-3 font-serif text-sm text-bone/70">{BUSINESS.address}</p>
          </div>
          <div className="flex flex-wrap gap-3">
            <Link to="/locations" className="border-2 border-bone px-6 py-4 font-mono text-xs uppercase tracking-[0.24em] text-bone transition-colors duration-200 hover:bg-bone hover:text-ink">
              Get Directions
            </Link>
            <Link to="/menu" className="border-2 border-bone bg-bone px-6 py-4 font-mono text-xs uppercase tracking-[0.24em] text-ink transition-colors duration-200 hover:bg-transparent hover:text-bone">
              See The Menu
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
